import { useState, useRef, useEffect } from 'react'
import { SUBTYPE_OPTIONS, FURNITURE_SIZE_LIMITS } from '../constants/furniture'

export default function FurnitureList({ furnitureList, collisionResult, onDelete, onSelect, selectedId, onUpdateSize, onUpdatePosition, onUpdateSubtype, onUpdateRotation, room, ignoredCollisions, onIgnoreCollision, onUnignoreCollision, hideHeader = false, onDuplicate }) {
    const [editingValue, setEditingValue] = useState(null)
    const [isOpen, setIsOpen] = useState(true)
    const [expandedSections, setExpandedSections] = useState({}) // { furnitureId: { position: bool, rotation: bool, size: bool } }
    const itemRefs = useRef({})
    const sectionRefs = useRef({}) // { `${furnitureId}-${section}`: element }

    // 選択されたアイテムの全タブ展開とスクロール
    useEffect(() => {
        if (selectedId) {
            // 全タブを展開
            setExpandedSections(prev => ({
                ...prev,
                [selectedId]: {
                    type: true,
                    'size_rotation': true
                }
            }))
            // 選択されたアイテムにスクロール
            setTimeout(() => {
                const element = itemRefs.current[selectedId]
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            }, 100)  // DOMが更新された後にスクロール
        }
    }, [selectedId])

    const toggleSection = (furnitureId, section) => {
        // 複数タブを同時に開ける
        setExpandedSections(prev => ({
            ...prev,
            [furnitureId]: {
                ...prev[furnitureId],
                [section]: !prev[furnitureId]?.[section]
            }
        }))
    }

    const handleValueClick = (furnitureId, field, currentValue) => {
        setEditingValue({ furnitureId, field, value: currentValue })
    }

    const handleValueChange = (e) => {
        const value = e.target.value
        // 空文字、マイナス記号のみ、またはマイナス記号付き数字を許可
        if (value === '' || value === '-' || /^-?\d+$/.test(value)) {
            setEditingValue({ ...editingValue, value })
        }
    }

    const handleValueBlur = () => {
        if (editingValue && editingValue.value !== '') {
            const numValue = Number(editingValue.value)
            if (isNaN(numValue)) {
                setEditingValue(null)
                return
            }
            if (editingValue.field.startsWith('size.')) {
                const dimension = editingValue.field.split('.')[1]
                onUpdateSize(editingValue.furnitureId, dimension, numValue)
            } else if (editingValue.field.startsWith('position.')) {
                const axis = editingValue.field.split('.')[1]
                onUpdatePosition(editingValue.furnitureId, axis, numValue)
            } else if (editingValue.field === 'rotation') {
                // -180〜180の範囲に制限
                const clampedValue = Math.max(-180, Math.min(180, numValue))
                onUpdateRotation && onUpdateRotation(editingValue.furnitureId, clampedValue)
            }
        }
        setEditingValue(null)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleValueBlur()
        } else if (e.key === 'Escape') {
            setEditingValue(null)
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            const currentValue = Number(editingValue.value) || 0
            const newValue = currentValue + 1
            setEditingValue({ ...editingValue, value: String(newValue) })

            // 上下矢印キーの場合はリアルタイムで反映
            if (editingValue.field.startsWith('size.')) {
                const dimension = editingValue.field.split('.')[1]
                onUpdateSize(editingValue.furnitureId, dimension, newValue)
            } else if (editingValue.field.startsWith('position.')) {
                const axis = editingValue.field.split('.')[1]
                onUpdatePosition(editingValue.furnitureId, axis, newValue)
            } else if (editingValue.field === 'rotation') {
                const clampedValue = Math.max(-180, Math.min(180, newValue))
                onUpdateRotation && onUpdateRotation(editingValue.furnitureId, clampedValue)
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault()
            const currentValue = Number(editingValue.value) || 0
            const newValue = currentValue - 1
            setEditingValue({ ...editingValue, value: String(newValue) })

            // 上下矢印キーの場合はリアルタイムで反映
            if (editingValue.field.startsWith('size.')) {
                const dimension = editingValue.field.split('.')[1]
                onUpdateSize(editingValue.furnitureId, dimension, newValue)
            } else if (editingValue.field.startsWith('position.')) {
                const axis = editingValue.field.split('.')[1]
                onUpdatePosition(editingValue.furnitureId, axis, newValue)
            } else if (editingValue.field === 'rotation') {
                const clampedValue = Math.max(-180, Math.min(180, newValue))
                onUpdateRotation && onUpdateRotation(editingValue.furnitureId, clampedValue)
            }
        }
    }

    const renderValue = (furnitureId, field, value, unit = 'cm') => {
        const isEditing = editingValue?.furnitureId === furnitureId && editingValue?.field === field

        if (isEditing) {
            return (
                <input
                    type="text"
                    value={editingValue.value}
                    onChange={handleValueChange}
                    onBlur={handleValueBlur}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    className="w-16 px-1 py-0 text-xs text-primary-400 bg-white/10 border border-primary-400 rounded text-right"
                />
            )
        }

        return (
            <span
                onClick={() => handleValueClick(furnitureId, field, value)}
                className="text-xs text-primary-400 cursor-pointer hover:text-primary-300 hover:underline w-14 text-right"
            >
                {value}{unit}
            </span>
        )
    }

    // hideHeaderがtrueの場合、常に展開状態でヘッダーなし
    const showContent = hideHeader || isOpen

    return (
        <div className={hideHeader ? "space-y-2" : "glass-effect rounded-2xl p-5 space-y-4"}>
            {/* ヘッダー - hideHeaderがfalseの場合のみ表示 */}
            {!hideHeader && (
                <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <div>
                        <h2 className="text-lg font-semibold text-white">配置済み家具</h2>
                        <p className="text-xs text-gray-400 mt-1">{furnitureList.length}個の家具</p>
                    </div>
                    <span className="text-white text-2xl">{isOpen ? '−' : '＋'}</span>
                </div>
            )}

            {showContent && (
                <div className={`space-y-2 ${hideHeader ? 'max-h-[350px]' : 'max-h-[calc(100vh-265px)]'} overflow-y-auto scrollbar-hide`}>
                    {furnitureList.length === 0 ? (
                        <div className={`text-center ${hideHeader ? 'py-4' : 'py-8'} text-gray-500 text-sm`}>家具を追加してください</div>
                    ) : (
                        furnitureList.map((furniture) => {
                            const hasCollision = collisionResult.collisions.has(furniture.id) &&
                                collisionResult.collisions.get(furniture.id).size > 0
                            const isOutOfBounds = collisionResult.outOfBounds.has(furniture.id)
                            const hasIssue = hasCollision || isOutOfBounds
                            const isSelected = furniture.id === selectedId

                            // モバイル（hideHeader）の場合はコンパクト表示
                            if (hideHeader) {
                                // 全ての干渉が無視されているかチェック
                                const collisionIds = Array.from(collisionResult.collisions.get(furniture.id) || [])
                                const allCollisionsIgnored = hasCollision && !isOutOfBounds && collisionIds.every(otherId => {
                                    const key = [furniture.id, otherId].sort().join(':')
                                    return ignoredCollisions.has(key)
                                })

                                // 枠の色を決定: 緑=問題なし, 黄=全て無視, 赤=干渉あり
                                const getBorderStyle = () => {
                                    if (!hasIssue) return 'bg-green-500/20 border border-green-500/50'
                                    if (allCollisionsIgnored) return 'bg-yellow-500/20 border border-yellow-500/50'
                                    return 'bg-red-500/20 border border-red-500/50'
                                }

                                return (
                                    <div
                                        key={furniture.id}
                                        ref={el => itemRefs.current[furniture.id] = el}
                                        className={`p-1 rounded-lg transition-all duration-200 ${getBorderStyle()} ${isSelected ? 'ring-2 ring-yellow-400' : ''}`}
                                    >
                                        {/* 1行目：アイコン + 名前 + ゴミ箱 */}
                                        <div
                                            className="flex justify-between items-center cursor-pointer"
                                            onClick={() => onSelect(furniture.id)}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="text-lg">{furniture.icon}</span>
                                                <span className="text-sm font-medium text-white">{furniture.name}</span>
                                            </div>
                                            <div className="flex gap-4">
                                                {onDuplicate && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            onDuplicate(furniture.id)
                                                        }}
                                                        className="text-gray-400 hover:text-blue-400 transition-colors flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5"
                                                        title="複製"
                                                    >
                                                        <span>📋</span>
                                                        <span className="text-xs">複製</span>
                                                    </button>
                                                )}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        onDelete(furniture.id)
                                                    }}
                                                    className="text-gray-400 hover:text-red-400 transition-colors flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5"
                                                    title="削除"
                                                >
                                                    <span>🗑️</span>
                                                    <span className="text-xs">削除</span>
                                                </button>
                                            </div>
                                        </div>

                                        {/* 選択時のみ詳細・編集パネル表示 */}
                                        {isSelected && (
                                            <div className="mt-2 pt-2 border-t border-white/10 space-y-2">
                                                {/* 干渉情報（干渉がある場合のみ表示） */}
                                                {hasIssue && (() => {
                                                    // 全ての干渉が無視されているかチェック
                                                    const collisionIds = Array.from(collisionResult.collisions.get(furniture.id) || [])
                                                    const allCollisionsIgnored = !isOutOfBounds && collisionIds.length > 0 && collisionIds.every(otherId => {
                                                        const key = [furniture.id, otherId].sort().join(':')
                                                        return ignoredCollisions.has(key)
                                                    })

                                                    return (
                                                        <div className={`p-2 rounded-lg border ${allCollisionsIgnored ? 'bg-yellow-500/10 border-yellow-500/50' : 'bg-red-500/10 border-red-500/50'}`}>
                                                            <div className={`text-xs font-bold mb-1 ${allCollisionsIgnored ? 'text-yellow-400' : 'text-red-400'}`}>
                                                                {allCollisionsIgnored ? '⚠️ 干渉情報（全て無視中）' : '⚠️ 干渉情報'}
                                                            </div>
                                                            <div className="text-xs space-y-1">
                                                                {isOutOfBounds && (
                                                                    <div className="text-red-400">⚠️ 部屋からはみ出し</div>
                                                                )}
                                                                {hasCollision && (
                                                                    <>
                                                                        {Array.from(collisionResult.collisions.get(furniture.id) || []).map(otherId => {
                                                                            const key = [furniture.id, otherId].sort().join(':')
                                                                            const isIgnored = ignoredCollisions.has(key)
                                                                            const otherFurniture = furnitureList.find(f => f.id === otherId)

                                                                            return (
                                                                                <div key={otherId} className="flex justify-between items-center">
                                                                                    <span className="text-gray-300">
                                                                                        {otherFurniture?.icon} {otherFurniture?.name}
                                                                                    </span>
                                                                                    <button
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation()
                                                                                            if (isIgnored) {
                                                                                                onUnignoreCollision(furniture.id, otherId)
                                                                                            } else {
                                                                                                onIgnoreCollision(furniture.id, otherId)
                                                                                            }
                                                                                        }}
                                                                                        className={`text-xs px-2 py-1 rounded ${isIgnored
                                                                                            ? 'bg-yellow-500/20 text-yellow-400'
                                                                                            : 'bg-gray-700 text-gray-400'
                                                                                            }`}
                                                                                    >
                                                                                        {isIgnored ? '無視中' : '無視'}
                                                                                    </button>
                                                                                </div>
                                                                            )
                                                                        })}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )
                                                })()}

                                                {/* タイプ選択 - 常時展開 */}
                                                <div className="bg-white/5 rounded-lg p-3">
                                                    <div className="text-xs text-gray-400 mb-2 font-bold">🎨 タイプ</div>
                                                    <select
                                                        value={furniture.subtype || 'default'}
                                                        onChange={(e) => onUpdateSubtype && onUpdateSubtype(furniture.id, e.target.value)}
                                                        className="w-full px-3 py-2 text-sm bg-gray-800 border border-white/20 rounded-lg text-white focus:outline-none focus:border-primary-400"
                                                    >
                                                        {SUBTYPE_OPTIONS[furniture.category]?.map(option => (
                                                            <option key={option.value} value={option.value} className="bg-gray-800 text-white">
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* サイズ・回転 - 常時展開・統合 */}
                                                <div className="bg-white/5 rounded-lg p-3 space-y-4">
                                                    <div className="text-xs text-gray-400 font-bold mb-2">📏 サイズ・回転</div>

                                                    {/* サイズ */}
                                                    <div className="space-y-3">
                                                        <div>
                                                            <div className="flex justify-between items-center mb-1">
                                                                <span className="text-xs text-gray-400">幅</span>
                                                                {renderValue(furniture.id, 'size.width', furniture.size.width)}
                                                            </div>
                                                            <input
                                                                type="range"
                                                                min={FURNITURE_SIZE_LIMITS.width.min}
                                                                max={FURNITURE_SIZE_LIMITS.width.max}
                                                                value={furniture.size.width}
                                                                onChange={(e) => {
                                                                    // スライダー操作は履歴に残さない（常に上書き）
                                                                    onUpdateSize(furniture.id, 'width', Number(e.target.value), true)
                                                                }}
                                                                className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
                                                            />
                                                        </div>
                                                        <div>
                                                            <div className="flex justify-between items-center mb-1">
                                                                <span className="text-xs text-gray-400">奥行き</span>
                                                                {renderValue(furniture.id, 'size.depth', furniture.size.depth)}
                                                            </div>
                                                            <input
                                                                type="range"
                                                                min={FURNITURE_SIZE_LIMITS.depth.min}
                                                                max={FURNITURE_SIZE_LIMITS.depth.max}
                                                                value={furniture.size.depth}
                                                                onChange={(e) => {
                                                                    onUpdateSize(furniture.id, 'depth', Number(e.target.value), true)
                                                                }}
                                                                className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
                                                            />
                                                        </div>
                                                        <div>
                                                            <div className="flex justify-between items-center mb-1">
                                                                <span className="text-xs text-gray-400">高さ</span>
                                                                {renderValue(furniture.id, 'size.height', furniture.size.height)}
                                                            </div>
                                                            <input
                                                                type="range"
                                                                min={FURNITURE_SIZE_LIMITS.height.min}
                                                                max={FURNITURE_SIZE_LIMITS.height.max}
                                                                value={furniture.size.height}
                                                                onChange={(e) => {
                                                                    onUpdateSize(furniture.id, 'height', Number(e.target.value), true)
                                                                }}
                                                                className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* 回転 */}
                                                    <div className="mt-2">
                                                        <div className="flex justify-between items-center mb-1">
                                                            <span className="text-xs text-gray-400">回転（角度）</span>
                                                            {renderValue(furniture.id, 'rotation', furniture.rotation || 0, '°')}
                                                        </div>
                                                        <input
                                                            type="range"
                                                            min={FURNITURE_SIZE_LIMITS.rotation.min}
                                                            max={FURNITURE_SIZE_LIMITS.rotation.max}
                                                            step="5"
                                                            value={furniture.rotation || 0}
                                                            onChange={(e) => {
                                                                onUpdateRotation && onUpdateRotation(furniture.id, Number(e.target.value), true)
                                                            }}
                                                            className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            }

                            // デスクトップ表示（従来通り）
                            // 全ての干渉が無視されているかチェック
                            const collisionIds = Array.from(collisionResult.collisions.get(furniture.id) || [])
                            const allCollisionsIgnored = hasCollision && !isOutOfBounds && collisionIds.every(otherId => {
                                const key = [furniture.id, otherId].sort().join(':')
                                return ignoredCollisions.has(key)
                            })

                            // 枠の色を決定: 緑=問題なし, 黄=全て無視, 赤=干渉あり
                            const getBorderStyleDesktop = () => {
                                if (!hasIssue) return 'bg-green-500/20 border border-green-500/50'
                                if (allCollisionsIgnored) return 'bg-yellow-500/20 border border-yellow-500/50'
                                return 'bg-red-500/20 border border-red-500/50'
                            }

                            return (
                                <div
                                    key={furniture.id}
                                    ref={el => itemRefs.current[furniture.id] = el}
                                    className={`p-1 rounded-xl transition-all duration-200 ${getBorderStyleDesktop()} ${isSelected ? 'ring-2 ring-yellow-400' : ''}`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div
                                            className="flex-1 cursor-pointer"
                                            onClick={() => onSelect(furniture.id)}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="text-xl">{furniture.icon}</span>
                                                <span className="text-sm font-medium text-white">{furniture.name}</span>
                                            </div>
                                            {hasIssue && (
                                                <div className="text-xs mt-2 space-y-1">
                                                    {isOutOfBounds && <div className="text-red-400">⚠️ 部屋からはみ出し</div>}
                                                    {hasCollision && (
                                                        <div className="border border-red-500/50 rounded-lg overflow-hidden">
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); toggleSection(furniture.id, 'collision'); }}
                                                                className="w-full flex justify-between items-center px-2 py-1 bg-red-500/10 text-red-400 text-xs font-medium"
                                                            >
                                                                <span>⚠️ 他の家具と干渉</span>
                                                                <span>{expandedSections[furniture.id]?.collision === false ? '＋' : '−'}</span>
                                                            </button>
                                                            {expandedSections[furniture.id]?.collision !== false && (
                                                                <div className="p-2 space-y-1">
                                                                    {Array.from(collisionResult.collisions.get(furniture.id) || []).map(otherId => {
                                                                        const key = [furniture.id, otherId].sort().join(':')
                                                                        const isIgnored = ignoredCollisions.has(key)
                                                                        const otherFurniture = furnitureList.find(f => f.id === otherId)

                                                                        return (
                                                                            <div key={otherId} className="flex items-center gap-2">
                                                                                <span className="text-gray-400">
                                                                                    {otherFurniture?.icon} {otherFurniture?.name}
                                                                                </span>
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation()
                                                                                        if (isIgnored) {
                                                                                            onUnignoreCollision(furniture.id, otherId)
                                                                                        } else {
                                                                                            onIgnoreCollision(furniture.id, otherId)
                                                                                        }
                                                                                    }}
                                                                                    className={`text-xs px-2 py-0.5 rounded ${isIgnored
                                                                                        ? 'bg-yellow-500/20 text-yellow-400'
                                                                                        : 'bg-gray-500/20 text-gray-400'
                                                                                        }`}
                                                                                >
                                                                                    {isIgnored ? '無視中' : '無視'}
                                                                                </button>
                                                                            </div>
                                                                        )
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex gap-2 items-start">
                                            {onDuplicate && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        onDuplicate(furniture.id)
                                                    }}
                                                    className="text-gray-400 hover:text-blue-400 transition-colors"
                                                    title="複製"
                                                >
                                                    📋
                                                </button>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    onDelete(furniture.id)
                                                }}
                                                className="text-gray-400 hover:text-red-400 transition-colors"
                                                title="削除"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )
                        })
                    )}
                </div>
            )}
        </div>
    )
}
